import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CreateTopicCommunitiesRequest,
  CreateTopicCommunityRequest,
  TopicCommunity,
  TopicCommunityListItem,
  TopicCommunityQuery,
  TopicCommunityType,
  UpdateNewsArticleTopicCommunityRequest,
  UpdateRecordingTopicCommunityRequest,
} from '@models/marketing/topic-communities/topic-community.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FileStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, forkJoin, of, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnRegexHandler, getAllWithParams, guidRegex, StateDictionary } from '../../helpers';
import { TopicCommunitiesService } from './topic-communities.service';

type TopicCommunitiesState = {
  topicCommunities: TopicCommunityListItem[];
  isLoading: boolean;
  isMultipleCreateLoading: boolean;
  isSingleCreateLoading: boolean;

  selectedTopicCommunityId: string | null; // topicId if community view; communityId if topic view

  isSelectedTopicCommunityLoading: boolean;
};

const initialState: TopicCommunitiesState = {
  topicCommunities: [],
  isLoading: false,
  isMultipleCreateLoading: false,
  isSingleCreateLoading: false,
  selectedTopicCommunityId: null,

  isSelectedTopicCommunityLoading: false,
};

const topicCommunitiesDictionary: StateDictionary<any> = {
  entityList: 'topicCommunities',
  listLoading: 'isLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: '',
  createLoading: '',
};

const updateSpecifiedTopicCommunity = (
  topicCommunities: TopicCommunityListItem[],
  updatedTopicCommunity: TopicCommunity,
  configToReplace: TopicCommunityType,
): TopicCommunityListItem[] => {
  return topicCommunities.map(item => {
    if (item.topicId === updatedTopicCommunity.topicId && item.communityId === updatedTopicCommunity.communityId) {
      return {
        ...item,
        ...(configToReplace === 'news-articles'
          ? { newsArticlesConfig: updatedTopicCommunity.newsArticlesConfig }
          : { recordingsConfig: updatedTopicCommunity.recordingsConfig }),
      };
    }
    return item;
  });
};

const addSpecifiedTopicCommunityId = (
  topicCommunities: TopicCommunityListItem[],
  createdTopicCommunity: TopicCommunity,
  configToAdd: TopicCommunityType,
): TopicCommunityListItem[] => {
  return topicCommunities.map(item => {
    if (item.topicId === createdTopicCommunity.topicId && item.communityId === createdTopicCommunity.communityId) {
      return {
        ...item,
        ...(configToAdd === 'news-articles'
          ? { newsArticlesConfig: { id: createdTopicCommunity.newsArticlesConfig!.id, name: null, pictureId: null } }
          : { recordingsConfig: { id: createdTopicCommunity.recordingsConfig!.id, name: null } }),
      };
    }
    return item;
  });
};

export const TopicCommunitiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('topic-communities-store'),

  withComputed(state => ({
    selectedTopicCommunity: computed(
      () =>
        state
          .topicCommunities()
          .find(tc => tc.topicId === state.selectedTopicCommunityId() || tc.communityId === state.selectedTopicCommunityId()) || null,
    ),
  })),

  withMethods(
    (
      store,
      topicCommunitiesService = inject(TopicCommunitiesService),
      router = inject(Router),
      fileStorageService = inject(FileStorageService),
      notificationsService = inject(NotificationsService),
    ) => {
      createCustomStateSetOnRegexHandler<TopicCommunitiesState>(new RegExp(`^/marketing/communities/${guidRegex}$`))(store, {
        topicCommunities: [],
        selectedTopicCommunityId: null,
      });
      createCustomStateSetOnRegexHandler<TopicCommunitiesState>(new RegExp(`^/marketing/topics/${guidRegex}$`))(store, {
        topicCommunities: [],
        selectedTopicCommunityId: null,
      });

      createCustomStateSetOnRegexHandler<TopicCommunitiesState>(new RegExp(`^/marketing/communities/${guidRegex}/topics$`))(store, {
        selectedTopicCommunityId: null,
      });
      createCustomStateSetOnRegexHandler<TopicCommunitiesState>(new RegExp(`^/marketing/topics/${guidRegex}/communities$`))(store, {
        selectedTopicCommunityId: null,
      });

      const repositoryMethods = {
        getAll: getAllWithParams<TopicCommunity, TopicCommunityQuery>({
          store,
          service: topicCommunitiesService,
          dictionary: topicCommunitiesDictionary,
        }),

        createMultiple: rxMethod<CreateTopicCommunitiesRequest>(
          pipe(
            tap(() => patchState(store, { isMultipleCreateLoading: true })),
            switchMap(request =>
              topicCommunitiesService.createMultiple(request).pipe(
                tap(response => {
                  patchState(store, {
                    topicCommunities: [...store.topicCommunities(), ...response],
                    isMultipleCreateLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isMultipleCreateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        createOne: rxMethod<{ type: 'recordings' | 'news-articles'; body: CreateTopicCommunityRequest }>(
          pipe(
            tap(() => patchState(store, { isSingleCreateLoading: true })),
            switchMap(({ type, body }) =>
              topicCommunitiesService.createOne(type, body).pipe(
                tap(response => {
                  patchState(store, {
                    topicCommunities: addSpecifiedTopicCommunityId(store.topicCommunities(), response, type),
                    isSingleCreateLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSingleCreateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        activateFileAndUpdateNewsArticleTopicCommunity: rxMethod<{
          file: { newId: string; oldId: string | null };
          entity: { id: string; body: UpdateNewsArticleTopicCommunityRequest };
        }>(
          pipe(
            tap(() => patchState(store, { isSelectedTopicCommunityLoading: true })),
            switchMap(({ file, entity }) =>
              fileStorageService.activateFile(file.newId).pipe(
                tap(() => {
                  patchState(store, {
                    isSelectedTopicCommunityLoading: false,
                  });

                  repositoryMethods.updateNewsArticleTopicCommunity({ ...entity, oldFileId: file.oldId });
                }),
                catchError(() => {
                  notificationsService.showNotification('Failed to upload file, please try again.');
                  patchState(store, { isSelectedTopicCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateNewsArticleTopicCommunity: rxMethod<{ id: string; body: UpdateNewsArticleTopicCommunityRequest; oldFileId?: string | null }>(
          pipe(
            tap(() => patchState(store, { isSelectedTopicCommunityLoading: true })),
            switchMap(({ id, body, oldFileId }) =>
              topicCommunitiesService.updateNewsArticleTopicCommunity(id, body).pipe(
                tap(response => {
                  patchState(store, {
                    topicCommunities: updateSpecifiedTopicCommunity(store.topicCommunities(), response, 'news-articles'),
                    isSelectedTopicCommunityLoading: false,
                  });
                  if (oldFileId) {
                    repositoryMethods.deleteFile({ id: oldFileId });
                  }
                }),
                catchError(() => {
                  patchState(store, { isSelectedTopicCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteFile: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedTopicCommunityLoading: true })),
            switchMap(({ id }) =>
              fileStorageService.deleteFile(id).pipe(
                tap(() => {
                  patchState(store, {
                    isSelectedTopicCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedTopicCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateRecordingsTopicCommunity: rxMethod<{ id: string; body: UpdateRecordingTopicCommunityRequest }>(
          pipe(
            tap(() => patchState(store, { isSelectedTopicCommunityLoading: true })),
            switchMap(({ id, body }) =>
              topicCommunitiesService.updateRecordingTopicCommunity(id, body).pipe(
                tap(response => {
                  patchState(store, {
                    topicCommunities: updateSpecifiedTopicCommunity(store.topicCommunities(), response, 'recordings'),
                    isSelectedTopicCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedTopicCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        delete: rxMethod<{ newsId: string | null; recordingsId: string | null }>(
          pipe(
            tap(() => patchState(store, { isSelectedTopicCommunityLoading: true })),
            switchMap(({ newsId, recordingsId }) =>
              forkJoin({
                deleteForNews: newsId ? topicCommunitiesService.delete('news-articles', newsId) : of(null),
                deleteForRecording: recordingsId ? topicCommunitiesService.delete('recordings', recordingsId) : of(null),
              }).pipe(
                tap(() => {
                  patchState(store, {
                    topicCommunities: store
                      .topicCommunities()
                      .filter(tc => tc.newsArticlesConfig?.id !== newsId && tc.recordingsConfig?.id !== recordingsId),
                    isSelectedTopicCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedTopicCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        selectTopicCommunity: (id: string) => {
          patchState(store, { selectedTopicCommunityId: id });
        },
        goToTopicCommunityActions: async (id: string, activatedRoute: ActivatedRoute): Promise<boolean> => {
          const success = await router.navigate([id], { relativeTo: activatedRoute });
          if (success) {
            if (store.selectedTopicCommunityId() !== id) navigationActions.selectTopicCommunity(id);
          }
          return success;
        },
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface TopicCommunitiesStoreType {
  readonly topicCommunities: Signal<TopicCommunityListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly isMultipleCreateLoading: Signal<boolean>;
  readonly isSingleCreateLoading: Signal<boolean>;
  readonly selectedTopicCommunityId: Signal<string | null>;
  readonly isSelectedTopicCommunityLoading: Signal<boolean>;

  readonly selectedTopicCommunity: Signal<TopicCommunityListItem | null>;

  readonly getAll: (params: TopicCommunityQuery) => void;
  readonly createMultiple: (request: CreateTopicCommunitiesRequest) => void;
  readonly createOne: (params: { type: 'recordings' | 'news-articles'; body: CreateTopicCommunityRequest }) => void;
  readonly activateFileAndUpdateNewsArticleTopicCommunity: (params: {
    file: { newId: string; oldId: string | null };
    entity: { id: string; body: UpdateNewsArticleTopicCommunityRequest };
  }) => void;
  readonly updateNewsArticleTopicCommunity: (params: {
    id: string;
    body: UpdateNewsArticleTopicCommunityRequest;
    oldFileId?: string | null;
  }) => void;
  readonly deleteFile: (params: { id: string }) => void;
  readonly updateRecordingsTopicCommunity: (params: { id: string; body: UpdateRecordingTopicCommunityRequest }) => void;
  readonly delete: (params: { newsId: string | null; recordingsId: string | null }) => void;
  readonly goToTopicCommunityActions: (id: string, activatedRoute: ActivatedRoute) => Promise<boolean>;
  readonly selectTopicCommunity: (id: string) => void;
}
